import { useEffect, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Modal, Form, Title, Descriptions, Text, Button } from '@bancobarigui/barihaus';

import { CancelForm } from './CancelForm';

import { InspectionRequest } from '@/services/types/inspection.types';
import { useCancelInspectionModal } from './useCancelInspectionModal';

interface CancelInspectionModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  inspectionData?: InspectionRequest | undefined;
}

export function CancelInspectionModal({
  open,
  onClose,
  onSuccess,
  inspectionData,
}: CancelInspectionModalProps) {
  const [showDetails, setShowDetails] = useState(false);

  const inspectionItemId = inspectionData?.items?.[0]?.id;

  const { form, isSubmitting, handleFormSubmit } = useCancelInspectionModal({
    onClose,
    onSuccess,
    inspectionItemId,
  });

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setShowDetails(false);
    }
  }, [open, form]);

  if (!inspectionData) return null;

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  return (
    <Modal
      centered
      open={open}
      width={900}
      footer={null}
      onCancel={onClose}
      className="space-y-8"
      closeIcon={<CloseOutlined />}
    >
      <div className="mb-6">
        <Title level={2}>Cancelar Inspeção - #{inspectionData.proposalNumber}</Title>
      </div>

      <div className="mb-4">
        <Button type="link" onClick={() => setShowDetails(!showDetails)} className="!p-0 underline">
          {showDetails ? 'Ocultar Detalhes' : 'Mostrar Detalhes'}
        </Button>
      </div>

      {showDetails && (
        <>
          <Descriptions
            column={{ xs: 1, sm: 2 }}
            bordered
            size="small"
            items={[
              {
                key: 'channel',
                label: <Text strong>Canal</Text>,
                children: inspectionData.channel || '-',
              },
              {
                key: 'proposalNumber',
                label: <Text strong>Nº da Proposta</Text>,
                children: inspectionData.proposalNumber || '-',
              },
              {
                key: 'createdAt',
                label: <Text strong>Solicitação</Text>,
                children: formatDate(inspectionData.createdAt),
              },
              {
                key: 'status',
                label: <Text strong>Status</Text>,
                children: inspectionData.status || '-',
                span: 2,
              },
            ]}
          />

          {inspectionData.items && inspectionData.items.length > 0 && (
            <div className="mt-6">
              <Title level={5}>Detalhes do Item</Title>
              {inspectionData.items.map((item, index) => (
                <Descriptions
                  key={index}
                  column={{ xs: 1, sm: 2 }}
                  bordered
                  size="small"
                  className="mt-4"
                  items={[
                    {
                      key: 'inspectionType',
                      label: <Text strong>Tipo de Inspeção</Text>,
                      children: item.inspectionTypeId || '-',
                    },
                    {
                      key: 'propertyType',
                      label: <Text strong>Tipo de Imóvel</Text>,
                      children: item.propertyTypeId || '-',
                    },
                    {
                      key: 'address',
                      label: <Text strong>Endereço</Text>,
                      children: `${item.street}, ${item.number} - ${item.district}, ${item.city}/${item.state}`,
                      span: 2,
                    },
                    {
                      key: 'cep',
                      label: <Text strong>CEP</Text>,
                      children: item.cep || '-',
                    },
                    {
                      key: 'complement',
                      label: <Text strong>Complemento</Text>,
                      children: item.complement || '-',
                    },
                    ...(item.iptuNumber
                      ? [
                          {
                            key: 'iptu',
                            label: <Text strong>Nº IPTU</Text>,
                            children: item.iptuNumber,
                          },
                        ]
                      : []),
                    ...(item.registrationNumber
                      ? [
                          {
                            key: 'registration',
                            label: <Text strong>Nº Matrícula</Text>,
                            children: item.registrationNumber,
                          },
                        ]
                      : []),
                    ...(item.registryOfficeNumber
                      ? [
                          {
                            key: 'registry',
                            label: <Text strong>Nº Cartório</Text>,
                            children: item.registryOfficeNumber,
                          },
                        ]
                      : []),
                    ...(item.landArea
                      ? [
                          {
                            key: 'landArea',
                            label: <Text strong>Área privativa (m²)</Text>,
                            children: item.landArea,
                          },
                        ]
                      : []),
                    ...(item.observation
                      ? [
                          {
                            key: 'observation',
                            label: <Text strong>Observação</Text>,
                            children: item.observation,
                            span: 2,
                          },
                        ]
                      : []),
                  ]}
                />
              ))}
            </div>
          )}

          {inspectionData.proponents && inspectionData.proponents.length > 0 && (
            <div className="mt-6">
              <Title level={5}>Proponente(s)</Title>
              {inspectionData.proponents.map((proponent, index) => (
                <Descriptions
                  key={index}
                  column={{ xs: 1, sm: 2 }}
                  bordered
                  size="small"
                  className="mt-4"
                  items={[
                    {
                      key: 'name',
                      label: <Text strong>Nome</Text>,
                      children: proponent.name || '-',
                      span: 2,
                    },
                    {
                      key: 'cpfCnpj',
                      label: <Text strong>CPF/CNPJ</Text>,
                      children: proponent.cpfCnpj || '-',
                    },
                    {
                      key: 'email',
                      label: <Text strong>E-mail</Text>,
                      children: proponent.email || '-',
                    },
                    {
                      key: 'phones',
                      label: <Text strong>Telefone(s)</Text>,
                      children:
                        proponent.phones && proponent.phones.length > 0
                          ? proponent.phones.map((p) => p.phoneNumber).join(', ')
                          : '-',
                      span: 2,
                    },
                  ]}
                />
              ))}
            </div>
          )}
        </>
      )}

      <div className="mt-6">
        <Title level={4}>Informações de Cancelamento</Title>

        <Form form={form} layout="vertical" onFinish={handleFormSubmit} className="mt-4">
          <CancelForm />

          <div className="flex justify-center gap-3 mt-6">
            <Button
              onClick={onClose}
              disabled={isSubmitting}
              size="large"
              style={{ borderColor: '#101010', color: '#101010', backgroundColor: 'transparent', boxShadow: 'none' }}
            >
              Manter
            </Button>
            <Button
              htmlType="submit"
              loading={isSubmitting}
              size="large"
              className="!border-0"
              style={{ backgroundColor: '#0090ff', color: '#fff', boxShadow: 'none' }}
            >
              Cancelar Inspeção
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
}
